import clsx from "clsx";

// Types
import { BASE_TABLE_PROPS } from "@/Washouts/constants";
import { IAdvisorsWashoutContentProps } from "@/Washouts/types/advisors";
import { IColumnDefinition, IWashoutModel } from "@/Washouts/types/commons";

// Helpers
import { and, or } from "@/Common/helpers/sonar";
import { hoursColDef, moneyColDef, numberColDef, objToRecord } from "@/Washouts/helpers/cards";
import { useIsWashoutEditable } from "@/Washouts/hooks/WashoutReview/useIsWashoutEditable";

// MUI
import { Grid, Typography } from "@mui/material";

// Tables
import CallActivityPerformanceWrapper from "@/Washouts/components/CallActivityPerformanceWrapper";
import EmployeeMetaSection from "@/Washouts/components/EmployeeMetaSection";
import AdvisorPerformanceTable from "@/Washouts/components/tables/Advisors/AdvisorPerformanceTable";
import ChargebacksTable from "@/Washouts/components/tables/Advisors/ChargebacksTable";
import Compensation from "@/Washouts/components/tables/Common/CompensationTable";
import InformationCard from "@/Washouts/components/tables/Common/InformationCard";
import LongevityCard from "@/Washouts/components/tables/Common/LongevityCard";
import SpiffsBonus from "@/Washouts/components/tables/Common/SpiffsBonusTable";

function CompensationWrapper(args: Readonly<IAdvisorsWashoutContentProps>) {
	const { model, selected, overrideCompensation, isLoading, handleSummaryChange } = args;

	const isEditable = useIsWashoutEditable({
		cycleStatus: selected?.status ?? "",
		positionStatus: model?.review?.status ?? "",
		hasRole: Boolean(overrideCompensation),
	});

	return (
		<Compensation
			data={model?.summary?.summary ?? []}
			addedData={model?.addedCompensations ?? []}
			tooltip
			editable={isEditable}
			columns={[
				{
					field: "gross",
					headerName: "Volume Gross",
					align: "right",
					headerAlign: "right",
					cellsType: "dollar",
					width: 120,
				},
				{
					field: "tier",
					headerName: "Eff. Inc. Rt.",
					align: "right",
					headerAlign: "right",
					cellsType: "percentage",
					round: 2,
					flex: 1,
				},
			]}
			overrideColumns={{
				item: {
					flex: 0,
					minWidth: 270,
					maxWidth: 270,
				},
			}}
			itemsDefinitions={[
				{ item: "Variable Gross Commissions", type: "percentage" },
				{ item: "Inbound Missed Calls", type: "percentage" },
			]}
			dataPresent={Boolean(model)}
			loading={isLoading}
			onActionUpdate={handleSummaryChange}
		/>
	);
}

const getStoreMetricsRows = (model: any) => {
	const { customerPay, internal, warranty, totals } = model?.storeMetrics ?? {};
	return [
		objToRecord({
			effectiveLaborRate: customerPay?.effectiveLaborRate ?? 0,
			averageHoursPerRepairOrder: customerPay?.averageHoursPerRepairOrder ?? 0,
			type: "Cust.",
		}),
		objToRecord({
			effectiveLaborRate: warranty?.effectiveLaborRate ?? 0,
			averageHoursPerRepairOrder: warranty?.averageHoursPerRepairOrder ?? 0,
			type: "Warr.",
		}),
		objToRecord({
			effectiveLaborRate: internal?.effectiveLaborRate ?? 0,
			averageHoursPerRepairOrder: internal?.averageHoursPerRepairOrder ?? 0,
			type: "Int.",
		}),
		objToRecord({
			effectiveLaborRate: totals?.effectiveLaborRate ?? 0,
			averageHoursPerRepairOrder: totals?.averageHoursPerRepairOrder ?? 0,
			type: "Total",
		}),
	];
};

function AdvisorsCardsSection({ model, stillLoading }: Readonly<IAdvisorsWashoutContentProps & { stillLoading: boolean }>) {
	const followUpPerformanceRows: Record<string, unknown>[] = [];
	const longevityRows = [];
	if (model?.longevity) {
		const values = { ...model?.longevity };
		longevityRows.push(values);
	}
	const hasLongevityCompensation = Boolean(longevityRows.at(0)?.compensation);
	const minRows = Math.max(followUpPerformanceRows.length, longevityRows.length, 4);
	const CELL_MARGIN_NO_COMPENSATION = 21;
	const CELL_MARGIN_COMPENSATION = 16;
	const cellMargin = hasLongevityCompensation ? CELL_MARGIN_COMPENSATION : CELL_MARGIN_NO_COMPENSATION;
	const followUpPerformanceHeightAdjustment = "4px";
	const elrRoRows = [
		objToRecord({
			effectiveLaborRate: +model?.performance?.totals?.customerPay?.effectiveLaborRate,
			averageHoursPerRepairOrder: +model?.performance?.totals?.customerPay?.averageHoursPerRepairOrder,
			type: "Cust.",
		}),
		objToRecord({
			effectiveLaborRate: +model?.performance?.totals?.warranty?.effectiveLaborRate,
			averageHoursPerRepairOrder: +model?.performance?.totals?.warranty?.averageHoursPerRepairOrder,
			type: "Warr.",
		}),
		objToRecord({
			effectiveLaborRate: +model?.performance?.totals?.internal?.effectiveLaborRate,
			averageHoursPerRepairOrder: +model?.performance?.totals?.internal?.averageHoursPerRepairOrder,
			type: "Int.",
		}),
		objToRecord({
			effectiveLaborRate: model?.effectiveLaborRate,
			averageHoursPerRepairOrder: model?.averageHoursPerRepairOrder,
			type: "Total",
		}),
	];

	const storeMetricsRoRows = getStoreMetricsRows(model);

	const elrCellRender = (row: Record<string, unknown>, _column: IColumnDefinition) => {
		const displayELR = Number(row?.effectiveLaborRate ?? 0).toFixed(2);
		return (
			<Grid container direction="row" justifyContent={"space-between"} marginTop={row?.type === "Total" ? "5px" : "0px"}>
				<Grid item>
					<Typography fontSize="14px" align={"left"} className="!font-[600] !font-montserrat relative truncate text-default">
						{String(row?.type)}
					</Typography>
				</Grid>
				<Grid item sx={{ marginRight: "-17px" }}>
					<Typography
						fontSize="14px"
						align={"right"}
						className="!font-[600] !font-montserrat relative truncate text-default before:content-['$'] before:absolute before:left-0"
						width={"70px"}
					>
						{displayELR}
					</Typography>
				</Grid>
			</Grid>
		);
	};
	const hrsPerRoCellRender = (row: Record<string, unknown>, _column: IColumnDefinition) => {
		const displayHrsRo = Number(row.averageHoursPerRepairOrder).toFixed(2);
		return (
			<Grid container direction="row" justifyContent={"space-between"} marginTop={row?.type === "Total" ? "5px" : "0px"}>
				<Grid item sx={{ marginLeft: "-17px" }}>
					<Typography
						fontSize="14px"
						align={"left"}
						marginLeft={"0px"}
						className="!font-[600] !font-montserrat relative truncate text-default"
					>
						{String(row?.type)}
					</Typography>
				</Grid>
				<Grid item>
					<Typography
						fontSize="14px"
						align={"right"}
						className="!font-[600] !font-montserrat relative truncate text-default"
						width={"77px"}
					>
						{displayHrsRo} hrs
					</Typography>
				</Grid>
			</Grid>
		);
	};
	const hrsPerRoHeaderRender = (column: IColumnDefinition) => {
		return (
			<Typography
				fontSize="14px"
				lineHeight="19.5px"
				align={column.alignHeader}
				className="text-default !font-[500] !font-montserrat"
				sx={{ marginLeft: "-17px" }}
			>
				{column.header}
			</Typography>
		);
	};

	return (
		<Grid
			container
			className={clsx("px-[30px] pb-[15px] pt-[32px] flex", {
				"justify-between": true,
			})}
		>
			<Grid item>
				<InformationCard
					title="Follow Up Performance"
					columns={[
						{
							...hoursColDef,
							field: "timeSinceLastContact",
							header: "Avg. Time Lapse Since Last Contact",
							width: 153,
						},
						{
							...numberColDef,
							alignHeader: "right",
							alignCell: "right",
							field: "closedRepairOrdersPerDay",
							header: "Avg. Closed ROs per Day",
							width: 103,
						},
					]}
					rows={[
						objToRecord({
							timeSinceLastContact: +model?.followUpPerformance?.averageTimeSinceLastContact,
							closedRepairOrdersPerDay: model?.followUpPerformance?.averageClosedRepairOrdersPerDay,
						}),
					]}
					loading={stillLoading}
					minRows={minRows}
					cardPadding={23}
					cellMargin={cellMargin}
					heightAdjustment={followUpPerformanceHeightAdjustment}
				/>
			</Grid>
			<Grid item>
				<InformationCard
					title="Individual ELR <br /> and RO Hours"
					columns={[
						{
							...moneyColDef,
							type: "text",
							field: "effectiveLaborRate",
							header: "ELR",
							width: 115,
							cellRender: elrCellRender,
						},
						{
							...numberColDef,
							type: "text",
							alignHeader: "left",
							alignCell: "right",
							field: "averageHoursPerRepairOrder",
							header: "Hrs/RO",
							width: 115,
							cellRender: hrsPerRoCellRender,
							columnRender: hrsPerRoHeaderRender,
						},
					]}
					rows={elrRoRows}
					loading={stillLoading}
					minRows={1}
					cardPadding={21}
					cellMargin={31}
				/>
			</Grid>
			<Grid item>
				<InformationCard
					title="Store ELR <br /> and RO Hours"
					columns={[
						{
							...moneyColDef,
							type: "text",
							field: "effectiveLaborRate",
							header: "ELR",
							width: 115,
							cellRender: elrCellRender,
						},
						{
							...numberColDef,
							type: "text",
							alignHeader: "left",
							alignCell: "right",
							field: "averageHoursPerRepairOrder",
							header: "Hrs/RO",
							width: 115,
							cellRender: hrsPerRoCellRender,
							columnRender: hrsPerRoHeaderRender,
						},
					]}
					rows={storeMetricsRoRows}
					loading={stillLoading}
					minRows={1}
					cardPadding={21}
					cellMargin={31}
				/>
			</Grid>
			<Grid item>
				<LongevityCard
					data={longevityRows}
					loading={stillLoading}
					minRows={minRows}
					cellMargin={cellMargin}
					heightAdjustment={"24px"}
				/>
			</Grid>
		</Grid>
	);
}

export default function AdvisorWashoutContent(props: Readonly<IAdvisorsWashoutContentProps>) {
	const {
		model = null,
		selected = null,
		isLoading = false,
		hideEmployeeMeta = false,
		overrideCompensation = false,
		handleSummaryChange = () => {},
		disableBoxShadow = false,
		locationId = "",
		positionWithoutSpaces = "",
	} = props;

	const callActivityPresent = and(!isLoading, Boolean(model), Boolean(model?.callActivityPerformance));
	const spiffsPresent = and(!isLoading, Boolean(model), Boolean(model?.spiffs));

	return (
		<Grid
			container
			direction="column"
			className={clsx({
				"pb-[30px]": true,
				"shadow-white-section": !disableBoxShadow,
				"min-h-[620px]": !model && isLoading,
				hidden: !model && !isLoading,
			})}
			rowSpacing={0}
		>
			<EmployeeMetaSection
				{...props.employeeMetaProps}
				model={model}
				hidden={hideEmployeeMeta}
				loading={isLoading}
				secondaryColumns={["DAYS_OUT", "RANKING"]}
			/>

			<AdvisorPerformanceTable
				key={`${model?._id}-advisor-performance`}
				data={model?.performance?.repairOrders ?? []}
				title="Performance"
				dataPresent={true}
				divider={true}
				loading={isLoading}
				tableProps={{ ...BASE_TABLE_PROPS, maxRows: 16 }}
			/>

			<AdvisorsCardsSection {...props} stillLoading={isLoading} />
			<Grid item container className="grid grid-cols-2 gap-x-[48px] gap-y-[32px] px-[30px] py-[15px] justify-end">
				{callActivityPresent && (
					<Grid item className="w-[646px]">
						<CallActivityPerformanceWrapper
							key={`${model?._id}-call-activity`}
							model={model as IWashoutModel}
							editable={or(selected?.status === "ACTIVE", selected?.status === "CLOSING")}
							dataPresent={callActivityPresent}
							loading={isLoading}
							onActionUpdate={handleSummaryChange}
							tableProps={{ ...BASE_TABLE_PROPS, maxRows: 6 }}
						/>
					</Grid>
				)}

				{spiffsPresent && (
					<Grid item className="w-[646px]">
						<SpiffsBonus
							key={`${model?._id}-spiffs`}
							data={model?.spiffs?.data}
							dataPresent={spiffsPresent}
							loading={isLoading}
							tableProps={{ ...BASE_TABLE_PROPS, maxRows: 6, enableRowsFiller: callActivityPresent ? true : undefined }}
						/>
					</Grid>
				)}

				<Grid item className="w-[646px]">
					<ChargebacksTable
						key={`${model?._id}-chargebacks`}
						title="Chargebacks"
						data={model?.chargebacks?.chargebacks ?? []}
						dataPresent={Boolean(model)}
						loading={isLoading}
						tableProps={{ ...BASE_TABLE_PROPS, maxRows: 10 }}
					/>
				</Grid>

				<Grid item className="w-[646px]">
					<CompensationWrapper
						model={model}
						selected={selected}
						overrideCompensation={overrideCompensation}
						isLoading={isLoading}
						handleSummaryChange={handleSummaryChange}
						locationId={locationId}
						positionWithoutSpaces={positionWithoutSpaces}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
}
